import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import publicApi from "@/lib/publicApi";
import usePageContent, { s } from "@/hooks/usePageContent";
import { parseLocaleFromPath } from "@/lib/locale";
import HCaptchaWidget from "@/components/HCaptchaWidget";

export default function Contact() {
  const { t } = useTranslation();
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const { data: page } = usePageContent("contact", locale);

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const captchaRef = useRef(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const hcaptcha_token = captchaRef.current?.getToken() || "";
      if (captchaRef.current?.isConfigured() && !hcaptcha_token) {
        setError(t("pages:contact.captcha_required") || "Please complete the captcha.");
        setBusy(false);
        return;
      }
      await publicApi.post("/contact", { ...form, locale, hcaptcha_token });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      captchaRef.current?.reset?.();
    } catch {
      setError(s(page, "form_error") || t("pages:contact.form_error"));
      captchaRef.current?.reset?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title={page?.meta_title} description={page?.meta_description} />
      <section className="bg-background grain-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">{s(page, "eyebrow")}</div>
            <h1 className="font-serif text-5xl lg:text-6xl text-foreground leading-[1.05]">{s(page, "title")}</h1>
            <p className="mt-5 text-lg text-foreground/75">{s(page, "subtitle")}</p>
          </div>

          <div className="mt-12 grid lg:grid-cols-[1.1fr_1.5fr] gap-10 lg:gap-16">
            <div className="space-y-7">
              <InfoBlock icon={MapPin} title={s(page, "address_title")}>
                <div>{t("footer.workshop")} {t("footer.owner")}</div>
                <div>{t("footer.address_line1")}</div>
                <div>{t("footer.address_line2")}</div>
              </InfoBlock>
              <InfoBlock icon={Phone} title={s(page, "phone_title")}>
                <a href={`tel:${t("footer.phone_landline_tel")}`} data-testid="contact-phone-landline" className="block hover:text-primary transition-colors">
                  <span className="text-muted-foreground text-[10px] uppercase tracking-[0.18em] mr-2">{t("footer.phone_landline_label")}</span>
                  {t("footer.phone_landline")}
                </a>
                <a href={`tel:${t("footer.phone_mobile_tel")}`} data-testid="contact-phone-mobile" className="block mt-1 hover:text-primary transition-colors">
                  <span className="text-muted-foreground text-[10px] uppercase tracking-[0.18em] mr-2">{t("footer.phone_mobile_label")}</span>
                  {t("footer.phone_mobile")}
                </a>
              </InfoBlock>
              <InfoBlock icon={Mail} title={s(page, "email_title")}>
                <a href={`mailto:${t("footer.email")}`} data-testid="contact-email" className="hover:text-primary transition-colors break-all">
                  {t("footer.email")}
                </a>
              </InfoBlock>
              <InfoBlock icon={Clock} title={s(page, "hours_title")}>
                <div className="whitespace-pre-line">{s(page, "hours_body")}</div>
              </InfoBlock>

              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">{s(page, "map_title")}</div>
                <div className="rounded-lg overflow-hidden border border-border/60 shadow-ambient" data-testid="contact-map">
                  <iframe
                    title="map"
                    src="https://maps.google.com/maps?q=Ivana+Rangera+44,+Varazdin,+Croatia&t=&z=15&ie=UTF8&iwloc=&output=embed"
                    className="w-full h-72 border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border/60 shadow-ambient p-7 lg:p-10">
              <h2 className="font-serif text-2xl text-foreground mb-1">{s(page, "form_title")}</h2>
              <div className="wood-divider w-16 mb-6" />
              {sent ? (
                <div data-testid="contact-success" className="flex items-start gap-3 rounded-lg bg-primary/10 border border-primary/30 p-5">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="font-serif text-xl text-foreground">{s(page, "form_success_title")}</div>
                    <p className="mt-1 text-sm text-foreground/80 leading-relaxed">{s(page, "form_success_body")}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="cn">{s(page, "form_name")}</Label>
                    <Input id="cn" required value={form.name} disabled={busy} onChange={onChange("name")} className="h-11 bg-background" data-testid="contact-name" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ce">{s(page, "form_email")}</Label>
                      <Input id="ce" type="email" required value={form.email} disabled={busy} onChange={onChange("email")} className="h-11 bg-background" data-testid="contact-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cs">{s(page, "form_subject")}</Label>
                      <Input id="cs" required value={form.subject} disabled={busy} onChange={onChange("subject")} className="h-11 bg-background" data-testid="contact-subject" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cm">{s(page, "form_message")}</Label>
                    <Textarea id="cm" required value={form.message} disabled={busy} onChange={onChange("message")} rows={6} className="bg-background resize-none" data-testid="contact-message" />
                  </div>
                  <HCaptchaWidget ref={captchaRef} />
                  {/* Honeypot — invisible to humans, irresistible to bots. */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.website}
                    onChange={onChange("website")}
                    className="absolute opacity-0 -left-[9999px] h-0 w-0 pointer-events-none"
                  />
                  {error && (
                    <div data-testid="contact-error" className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
                      {error}
                    </div>
                  )}
                  <Button type="submit" disabled={busy} data-testid="contact-submit" className="h-11 shadow-interactive">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
                    {busy ? s(page, "form_sending") : s(page, "form_submit")}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function InfoBlock({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="rounded-lg bg-primary/10 p-2.5">
        <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">{title}</div>
        <div className="text-sm text-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
